import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";
import { ReportDownloadProvider } from "@/lib/ReportDownloadProvider";

import PublicLayout from "@/components/PublicLayout";
import LandingPage from "@/pages/public/LandingPage";
import AboutPage from "@/pages/public/AboutPage";
import FaqPage from "@/pages/public/FaqPage";
import ContactPage from "@/pages/public/ContactPage";
import LoginPage from "@/pages/public/LoginPage";

import AdminLayout from "@/components/AdminLayout";
import AdminHome from "@/pages/admin/AdminHome";
import AdminTraining from "@/pages/admin/AdminTraining";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminEmployees from "@/pages/admin/AdminEmployees";
import AdminEmployeeResult from "@/pages/admin/AdminEmployeeResult";
import AdminForensic from "@/pages/admin/AdminForensic";
import AdminSubmissionDetail from "@/pages/admin/AdminSubmissionDetail";
import AdminPages from "@/pages/admin/AdminPages";
import AdminViolations from "@/pages/admin/AdminViolations";
import AdminBackup from "@/pages/admin/AdminBackup";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminSettings from "@/pages/admin/AdminSettings";

import CandidateLayout from "@/components/CandidateLayout";
import CandidateHome from "@/pages/candidate/CandidateHome";
import CandidateTraining from "@/pages/candidate/CandidateTraining";
import CandidateTyping from "@/pages/candidate/CandidateTyping";
import CandidateResults from "@/pages/candidate/CandidateResults";
import CandidateResultDetail from "@/pages/candidate/CandidateResultDetail";

import PageTitle from "@/components/PageTitle";

import "@/App.css";

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const isAdmin = user.role === "admin" || user.role === "super_admin";
  const isCandidate = user.role === "candidate";
  if (role === "admin" && !isAdmin) return <Navigate to="/dashboard" replace />;
  if (role === "candidate" && !isCandidate) return <Navigate to="/admin" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App grain">
      <I18nProvider>
        <AuthProvider>
          <ReportDownloadProvider>
          <BrowserRouter>
            <PageTitle />
            <Toaster position="top-right" richColors />
            <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/contact" element={<ContactPage />} />
            </Route>
            <Route path="/login" element={<LoginPage />} />

            <Route path="/admin" element={<Protected role="admin"><AdminLayout /></Protected>}>
              <Route index element={<AdminHome />} />
              <Route path="training" element={<AdminTraining />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="employees" element={<AdminEmployees />} />
              <Route path="employees/:id/result" element={<AdminEmployeeResult />} />
              <Route path="employees/:id/forensic" element={<AdminForensic />} />
              <Route path="submissions/:sid" element={<AdminSubmissionDetail />} />
              <Route path="pages" element={<AdminPages />} />
              <Route path="violations" element={<AdminViolations />} />
              <Route path="backup" element={<AdminBackup />} />
              <Route path="projects" element={<AdminProjects />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="/dashboard" element={<Protected role="candidate"><CandidateLayout /></Protected>}>
              <Route index element={<CandidateHome />} />
              <Route path="training" element={<CandidateTraining />} />
              <Route path="typing" element={<CandidateTyping />} />
              <Route path="results" element={<CandidateResults />} />
              <Route path="results/:sid" element={<CandidateResultDetail />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          </ReportDownloadProvider>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}
